/**
 * @fileoverview 防重复点击的组合式函数
 *
 * 这个 composable 提供了防抖功能，可以防止用户在短时间内重复点击按钮或触发事件。
 *
 * @example 基本使用
 * ```typescript
 * const { handleClick, debouncing } = useDebounceClick(() => {
 *   console.log('按钮被点击了')
 * })
 *
 * // 在模板中使用
 * // <button @click="handleClick">
 * //   {{ debouncing ? '处理中...' : '点击我' }}
 * // </button>
 * ```
 *
 * @example 处理事件参数
 * ```typescript
 * const { handleClick } = useDebounceClick((event: MouseEvent) => {
 *   console.log('点击位置:', event.clientX, event.clientY)
 *   emit('click', event)
 * })
 * ```
 *
 * @example 自定义防抖时间
 * ```typescript
 * const { handleClick, debouncing } = useDebounceClick(() => {
 *   console.log('按钮被点击了')
 * }, 1000) // 1秒防抖
 * ```
 *
 * @example 避免加载很快时的UI闪烁
 * ```typescript
 * const { handleClick, debouncing } = useDebounceClick(async () => {
 *   await api.quickAction()
 * })
 *
 * // 防抖期间不显示 loading 状态，避免闪烁
 * const loading = computed(() => {
 *   return props.loading && !debouncing.value
 * })
 * ```
 *
 * @example 禁用防抖
 * ```typescript
 * const { handleClick } = useDebounceClick(() => {
 *   console.log('立即执行')
 * }, 0) // 设置为 0 禁用防抖
 * ```
 */

import { ref } from 'vue'

/**
 * 防重复点击的 composable
 * @param callback 点击时执行的回调函数
 * @param interval 防抖间隔时间（毫秒），默认 300ms，为 0 时不防抖
 * @returns 包含处理函数和防抖状态的对象
 *   - handleClick: 处理点击事件的函数
 *   - debouncing: 防抖状态，可用于控制UI显示，避免快速加载时的闪烁
 */
export function useDebounceClick<T extends unknown[]>(
  callback: (...args: T) => void,
  interval: number = 300
) {
  const debouncing = ref(false)

  const handleClick = (...args: T) => {
    if (debouncing.value) return

    if (interval === 0) {
      callback(...args)
    } else {
      debouncing.value = true
      callback(...args)

      setTimeout(() => {
        debouncing.value = false
      }, interval)
    }
  }

  return {
    handleClick,
    debouncing,
  }
}
